import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getClassesAPI,
} from "../../../services/portal/admin/classService";
import { formatAmount, formatDate } from "../../../utility/Common";
import Loader from "../../../components/Loader";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import ClassDetailsBox from "../../../components/portal/ClassDetailsBox";
import {
  getPayedInformationAPI,
} from "../../../services/portal/student/studentService";
import { useAuth } from "../../../contexts/AuthContext";
import { RoutePaths } from "../../../utility/RoutePaths";

const ClassDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const classId = searchParams.get("classId");
  const [loading, setLoading] = useState(false);
  const [classDetails, setClassDetails] = useState(null);
  const [monthList, setMonthList] = useState([]);

  const loadClassDetails = useCallback(async () => {
    if (!classId) return;

    setLoading(true);
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        const classItem = res.Data.find((c) => c.ClassId === Number(classId));
        if (classItem) {
          setClassDetails(classItem);
        } else {
          showErrorToastMsg("Class not found");
        }
      }
    } catch (error) {
      showErrorToastMsg("Failed to load class details");
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    if (classId) {
      loadClassDetails();
    }
  }, [classId, loadClassDetails]);

  useEffect(() => {
    if (user) {
      loadPayment();
    }
  }, [user]);

  const loadPayment = async () => {
    setLoading(true);
    const res = await getPayedInformationAPI(classId);
    if (res && res.IsSuccessful && res.Data) {
      setMonthList(res.Data);
    }
    setLoading(false);
  };

  if (!classId) {
    return (
      <DashboardLayout title="Class Schedule" subtitle="Class not found">
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">Class ID is required</p>
        </div>
      </DashboardLayout>
    );
  }

  const showMonthlyCard = (item) => {
    const date = formatDate(item.FirstDayOfMonth);
    const paymentStatus =
      item.BankSlipStatus === "Approved"
        ? 2
        : item.BankSlipStatus === "Active"
          ? 1
          : item.BankSlipStatus === "Inactive"
          ? 9
          : 0;
    const isPaid = item.IsPayed;
    const statusColor =
      paymentStatus === 1
        ? "bg-yellow-100 text-yellow-800 border-yellow-200"
        : paymentStatus === 2
          ? "bg-green-100 text-green-800 border-green-200"
          : "bg-primary-100 text-primary-800 border-primary-200";

    const boxBorderColor =
      paymentStatus === 1
        ? "border-yellow-500"
        : paymentStatus === 2
          ? "border-green-500"
          : "border-primary-500";

    const iconBgColor =
      paymentStatus === 1
        ? "bg-yellow-100"
        : paymentStatus === 2
          ? "bg-green-100"
          : "bg-primary-100";

    const iconTextColor =
      paymentStatus === 1
        ? "text-yellow-600"
        : paymentStatus === 2
          ? "text-green-600"
          : "text-primary-600";

    return (
      <div
        key={item.MonthYear}
        className={`bg-white rounded-lg shadow-md border-l-4 cursor-pointer ${boxBorderColor}
        p-6 hover:shadow-lg transition-all duration-300`}
        onClick={() => {
          navigate(
            `${RoutePaths.STUDENT_CLASS_MONTH_DETAILS_PAGE}?classId=${classId}&date=${date}`,
          );
        }}
      >
        <div className="flex flex-col h-full">
          {/* Header with Month and Status */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${iconBgColor}`}
              >
                <svg
                  className={`w-5 h-5 ${iconTextColor}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">
                  {item.MonthYear}
                </p>
              </div>
            </div>
            <span
              className={`text-xs font-semibold px-3 py-1 rounded-full border ${statusColor}`}
            >
              {paymentStatus === 1
                ? "Pending Approval"
                : paymentStatus === 9
                ? "Pending Online Approval"
                : paymentStatus === 2
                  ? "Paid"
                  : "Not Paid"}
            </span>
          </div>

          {/* Description */}
          <div className="mb-4">
            <p className="text-sm text-gray-600 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Online Sessions
            </p>
          </div>

          {/* Amount */}
          <div className="mt-auto pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Monthly Fee | මාසික ගාස්තුව
              </span>
              <span className={`text-lg font-bold ${iconTextColor}`}>
                {formatAmount(classDetails.ClassFee, true)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout
      title="Class Schedule"
      subtitle="View class details and schedules"
    >
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      ) : !classDetails ? (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg"></p>
        </div>
      ) : (
        <>
          <ClassDetailsBox classDetails={classDetails} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {monthList.map((item) => showMonthlyCard(item))}
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default ClassDetailsPage;
